import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Receipt, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';
import { ReportHeader, DateRange, StatCard, SourceToggle, fyStart, today } from './_shared';
import EmailReportButton from './EmailReportButton';
import ReceivablesPanel from './ReceivablesPanel';

const InvoicesReport = () => {
  const { authHeader } = useAuth();
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(today());
  const [source, setSource] = useState('gst');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      params.set('source', source);
      const { data: d } = await axios.get(`${API}/reports/invoices?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, from, to, source]);

  useEffect(() => { load(); }, [load]);

  const combined = data?.combined || {};
  const legacy = data?.legacy || {};
  const gst = data?.gst || {};
  const bannerTitle = source === 'gst' ? 'GST Invoices'
    : source === 'legacy' ? 'Legacy Invoices (Non-GST)'
    : 'Combined (Legacy + GST)';

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="report-invoices">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ReportHeader icon={Receipt} title="Invoices Report" />
        <EmailReportButton
          reportType="invoices"
          getFilters={() => ({ date_from: from, date_to: to, source })}
        />
      </div>
      <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={load} />

      {/* Source selector — GST-only is the default view */}
      <SourceToggle value={source} onChange={setSource} idPrefix="invrep-source" />

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      {/* Headline totals for the selected source */}
      <div className="bg-gradient-to-br from-teal-500 to-emerald-600 text-white rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1" data-testid="invrep-banner-title">{bannerTitle}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Invoices</div>
            <div className="text-2xl font-bold" data-testid="invrep-comb-count">{combined.count ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Billed</div>
            <div className="text-2xl font-bold font-mono" data-testid="invrep-comb-billed">{fmtINR(combined.billed)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Paid</div>
            <div className="text-2xl font-bold font-mono">{fmtINR(combined.paid)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Outstanding</div>
            <div className="text-2xl font-bold font-mono">{fmtINR(combined.outstanding)}</div>
          </div>
        </div>
        <div className="mt-3 text-[11px] opacity-80" data-testid="invrep-draft-note">
          Drafts and cancelled invoices are excluded
          {combined.draft_count ? ` — ${combined.draft_count} draft${combined.draft_count === 1 ? '' : 's'} in this period not counted` : ''}
          . Amounts cover invoices dated in the selected period only; everything still
          owed to you is in <b>Payments pending</b> below.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {/* Legacy */}
        {source !== 'gst' && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Legacy Invoices</h2>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <StatCard label="Count" value={legacy.count ?? 0} mono={false} testid="invrep-legacy-count" />
            <StatCard label="Billed" value={fmtINR(legacy.billed)} color="text-slate-900" />
            <StatCard label="Outstanding" value={fmtINR(legacy.outstanding)} color={legacy.outstanding > 0 ? 'text-red-700' : 'text-emerald-700'} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(legacy.by_status || {}).map(([st, n]) => (
              <span key={st} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{st}: <b>{n}</b></span>
            ))}
          </div>
        </div>
        )}
        {/* GST */}
        {source !== 'legacy' && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">GST Invoices</h2>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <StatCard label="Count" value={gst.count ?? 0} mono={false} testid="invrep-gst-count" />
            <StatCard label="Billed" value={fmtINR(gst.billed)} color="text-slate-900" />
            <StatCard label="Tax collected" value={fmtINR(gst.tax_collected)} color="text-amber-700" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(gst.by_status || {}).map(([st, n]) => (
              <span key={st} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{st}: <b>{n}</b></span>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Money still owed to us — all time, independent of the date filter */}
      <ReceivablesPanel source={source} />

      {/* Top customers */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">
            Top customers (by billed)
            <span className="ml-1.5 text-xs font-normal text-slate-500">
              — in the selected period only
            </span>
          </h2>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : (data?.top_customers || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No invoices in this period.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-100 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600">Customer</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Invoices</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Billed</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Paid</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.top_customers.map((c) => (
                <tr key={c.name} className="hover:bg-slate-50" data-testid={`invrep-cust-${c.name.replace(/[^a-z0-9]/gi, '-').slice(0, 25)}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.invoices}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{fmtINR(c.billed)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtINR(c.paid)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-bold ${c.outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtINR(c.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InvoicesReport;
